/**
 * Rider Skills Rubric Type Definitions
 *
 * Type definitions for the per-group skills assessment system.
 * - Skills configuration (admin CRUD)
 * - Skills assessment (coach evaluation)
 * - Derived types for progression and group readiness
 *
 * Requirements: 1.1, 2.3, 4.1, 5.3, 6.1, 6.2
 */

/**
 * A skill configured for a ride group.
 * Stored in `skills_config_db` with group_name as partition key and skill_id as sort key.
 */
export interface SkillConfig {
  skill_id: string;
  group_name: string;
  section: string;       // "Technical Riding Skills" | "Mental/Attitude Skills" | "Fitness/Endurance Skills"
  category: string;
  skill_name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

/**
 * DTO for creating a new skill config entry.
 * Frontend generates the skill_id via crypto.randomUUID().
 */
export interface CreateSkillConfigDto {
  skill_id: string;
  group_name: string;
  section: string;
  category: string;
  skill_name: string;
  description: string;
}

/**
 * DTO for updating an existing skill config entry.
 * Uses skill_id + group_name as composite key.
 */
export interface UpdateSkillConfigDto {
  skill_id: string;
  group_name: string;
  skill_name?: string;
  description?: string;
}

/**
 * DTO for deleting a single skill config entry.
 */
export interface DeleteSkillConfigDto {
  skill_id: string;
  group_name: string;
}

/**
 * DTO for deleting all skills in a category for a group.
 */
export interface DeleteSkillCategoryDto {
  group_name: string;
  section: string;
  category: string;
}

/**
 * A recorded assessment of a rider on a single skill.
 * Stored in `skills_assessment_db` with rider_id as partition key
 * and skill_timestamp ({skill_id}#{timestamp}) as sort key.
 */
export interface SkillAssessment {
  rider_id: string;
  skill_id: string;
  skill_timestamp: string;
  assessment_level: 'Beginner' | 'Intermediate' | 'Advanced';
  assessed_at: string;
  assessed_by: string;
}

/**
 * DTO for creating a new skill assessment.
 * The backend generates assessed_at and composes the sort key.
 */
export interface CreateSkillAssessmentDto {
  rider_id: string;
  skill_id: string;
  assessment_level: 'Beginner' | 'Intermediate' | 'Advanced';
  assessed_by: string;
}

/** Display labels shown to users — mapped from stored assessment level values. */
export type AssessmentLevelDisplayName = 'Learning It' | 'Dialing It In' | 'Sending It';

/** Single source of truth for stored-value → display-label mapping. */
export const ASSESSMENT_LEVEL_DISPLAY_MAP: Record<
  'Beginner' | 'Intermediate' | 'Advanced',
  AssessmentLevelDisplayName
> = {
  Beginner: 'Learning It',
  Intermediate: 'Dialing It In',
  Advanced: 'Sending It',
} as const;

/**
 * System-derived recommendation for whether a rider is ready
 * to advance, stay, or needs more time in their current group.
 */
export interface GroupReadiness {
  suggestion: 'Ready to advance' | 'On track' | 'Needs more time' | 'Insufficient assessments' | 'Top group — mastery achieved';
  targetGroup?: string;
  assessedCount: number;
  totalCount: number;
  masteryPercent: number;
  timeTrialAdjustment?: string;
}

/**
 * Admin-configurable thresholds for the recommendation engine.
 * Stored in `skills_config_db` with key { group_name: "__config__", skill_id: "recommendation_thresholds" }.
 *
 * Requirements: 1.4, 3.5
 */
export interface ThresholdSettings {
  mastery_threshold: number;
  needs_improvement_threshold: number;
}

/**
 * Default threshold values used when no persisted configuration exists.
 */
export const DEFAULT_THRESHOLDS: ThresholdSettings = {
  mastery_threshold: 80,
  needs_improvement_threshold: 50,
};

/**
 * Zone classification for a rider's time-trial performance relative to their group.
 *
 * Requirements: 3.1, 3.2
 */
export type TimeTrialZone =
  | 'Behind Group'
  | 'With Group'
  | 'Ahead of Group'
  | 'Ahead of Group+'
  | 'No Data'
  | 'Insufficient Group Data';

/**
 * Cross-group comparison details when zone is "Ahead of Group+".
 *
 * Requirements: 6.8
 */
export interface CrossGroupComparison {
  higher_group_name: string;
  riders_beaten: number;
  higher_group_rider_count: number;
}

/**
 * Response from GET /time-trials/zone endpoint.
 *
 * Requirements: 4.2
 */
export interface TimeTrialZoneResponse {
  zone: TimeTrialZone;
  rider_time: number | null;
  group_median: number | null;
  group_rider_count: number;
  ahead_threshold: number | null;
  behind_threshold: number | null;
  season: string | null;
  cross_group: CrossGroupComparison | null;
}


/**
 * Skills Report API Response Types
 *
 * Types for the GET /skills-report endpoint that returns
 * riders ranked by computed Skills Score within each ride group.
 *
 * Requirements: 2.1, 4.4
 */

/**
 * A single rider entry in the skills report.
 */
export interface SkillsReportRider {
  rider_id: string;
  name: string;
  group: string;
  /** Count of skills assessed at Intermediate or Advanced */
  skills_score: number;
  /** Total number of skills configured for the rider's group */
  max_possible_score: number;
  /** (skills_score / max_possible_score) × 100 */
  skills_percentage: number;
  assessed_count: number;
  total_skills: number;
  group_readiness: {
    suggestion: string;
    assessed_count: number;
    total_count: number;
    mastery_percent: number;
  };
  time_trial_zone?: {
    zone: TimeTrialZone;
    rider_time: number | null;
    group_median: number | null;
    group_rider_count: number;
    cross_group: CrossGroupComparison | null;
  };
}

/**
 * A group section in the skills report containing its riders.
 */
export interface SkillsReportGroup {
  group_name: string;
  group_order: number;
  skill_count: number;
  /** Total number of skills configured for this group */
  max_possible_score: number;
  riders: SkillsReportRider[];
  skill_averages?: SkillAverage[];
}

/**
 * Top-level response from GET /skills-report.
 */
export interface SkillsReportResponse {
  groups: SkillsReportGroup[];
}



/**
 * Per-skill average data returned by the extended GET /skills-report endpoint.
 * Included in each SkillsReportGroup as skill_averages[].
 *
 * Requirements: 6.2, 6.9
 */
export interface SkillAverage {
  skill_id: string;
  skill_name: string;
  description: string;
  section: string;
  category: string;
  average_level: number;          // 0-3 scale (0 = no assessments)
  assessed_rider_count: number;
  at_target_count: number;        // riders at Advanced
  at_target_percent: number;      // (at_target_count / assessed_rider_count) * 100, Advanced only
  weighted_skill_score: number;   // sum of level values (Beginner=1, Intermediate=2, Advanced=3)
  level_distribution: {
    beginner: number;
    intermediate: number;
    advanced: number;
  };
}

/**
 * Score band distribution for the distribution chart.
 *
 * Requirements: 7.1
 */
export interface ScoreBandDistribution {
  belowTarget: number;    // < 50%
  mostAtTarget: number;   // 50-99%
  allAtTarget: number;    // 100%
}

/**
 * A rider flagged for coach attention.
 *
 * Requirements: 8.2
 */
export interface NeedsAttentionRider {
  rider_id: string;
  name: string;
  reason: string;  // e.g., "Declining" or "Only 3/12 skills assessed"
}
